import CustomDrawer from 'components/commom/CustomDrawer';
import { useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { CircularProgress, IconButton } from '@mui/material';
import LabelWithInput from 'components/commom/LabelWithPracticeInput';
import 'assets/styles/common.scss';
import { useDispatch, useSelector } from 'react-redux';
import CommonToast from 'components/commom/CommonToast';
import { useNavigate } from 'react-router-dom';
import { ClearTokenRedirectLogin } from 'utils/general';
import { createLeadCaptureCallAPI } from 'store/LeadCapture/createLeadCapture/action';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import LabelWithTextArea from 'components/commom/LabelWithTextArea';
import CustomStepper from 'components/commom/CustomStepper';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { updateLeadCaptureCallAPI } from 'store/LeadCapture/updateLeadCapture/action';

const ChipSelector = ({
  label,
  slug,
  formKey,
  formValue,
  dropdownData,
  onChange,
}) => {
  const values =
    dropdownData?.find((item) => item?.slug === slug)?.values || [];
  return (
    <div className="mt-[24px]">
      <div className="text-[14px] lh-201 family_outfit_medium_500 fc-292D32 mb-[4px]">
        {label}
      </div>
      <div className="mt-[8px] flex flex-wrap gap-[10px]">
        {values.map((data, index) => (
          <div
            key={index}
            className={`p-[10px] text-[14px] family_outfit_medium_500 lh-16 h-[45px] min-w-[150px] cursor-pointer flex items-center justify-center border-1 border-[#998A7B] ${formValue?.[formKey]?.slug === data?.slug ? 'bg-[#874529] text-white' : 'text-[#874529]'}`}
            onClick={() => onChange(formKey, data)}
          >
            <div>{data?.name}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const MultiChipSelector = ({
  label,
  slug,
  formKey,
  formValue,
  dropdownData,
  onChange,
}) => {
  const values =
    dropdownData?.find((item) => item?.slug === slug)?.values || [];
  const selected = formValue?.[formKey] || [];
  return (
    <div className="mt-[24px]">
      <div className="text-[14px] lh-201 family_outfit_medium_500 fc-292D32 mb-[4px]">
        {label}
      </div>
      <div className="mt-[8px] flex flex-wrap gap-[10px]">
        {values.map((data, index) => {
          const isSelected = selected.some((s) => s?.slug === data?.slug);
          return (
            <div
              key={index}
              className={`p-[10px] text-[14px] family_outfit_medium_500 lh-16 h-[45px] min-w-[150px] cursor-pointer flex items-center justify-center border-1 border-[#998A7B] ${isSelected ? 'bg-[#874529] text-white' : 'text-[#874529]'}`}
              onClick={() => onChange(formKey, data)}
            >
              <div>{data?.name}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CreateLead = ({ open, onClose, editData }) => {
  const dispatch = useDispatch();
  const { showToast } = CommonToast();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const profileImageInputRef = useRef(null);
  const [formValue, setFormValue] = useState({
    fullName: '',
    company: '',
    titleRole: '',
    email: '',
    definesYouBest: null,
    practiceType: null,
    currentlyOfferTeledermatology: null,
    estimatedWeeklyPatientVolume: null,
    interestAbsNetwork: 5,
    interestAAD: null,
    interestLevel: null,
    notes: '',
    followup: '',
    businessCard: null,
    businessCardPreview: null,
    profileImage: null,
    profileImagePreview: null,
    investmentFocus: [],
    investorDeck: null,
    orgType: [],
  });
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('');

  const leadCaptureDropdownData = useSelector(
    (state) => state?.leadCaptureDropdownReducer?.users?.data
  );
  const leadDetail = useSelector(
    (state) => state?.getLeadCaptureDetailReducer?.users?.data
  );
  const createLeadLoading = useSelector(
    (state) => state?.createLeadCaptureReducer?.loading
  );
  const updateLeadLoader = useSelector(
    (state) => state?.updateLeadCaptureReducer?.loading
  );

  useEffect(() => {
    if (open) {
      initialState();
    }
  }, [open]);

  const initialState = () => {
    setFormValue({
      fullName: editData?.id ? leadDetail?.full_name : '',
      company: editData?.id ? leadDetail?.company_organization : '',
      titleRole: editData?.id ? leadDetail?.title_role : '',
      email: editData?.id ? leadDetail?.email : '',
      definesYouBest: editData?.id
        ? leadDetail?.user_type
        : leadCaptureDropdownData?.find((item) => item?.slug === 'user-type')
            ?.values?.[0] || null,
      practiceType: editData?.id
        ? leadDetail?.practice_type
        : leadCaptureDropdownData?.find(
            (item) => item?.slug === 'practice-type'
          )?.values?.[0] || null,
      currentlyOfferTeledermatology: editData?.id
        ? leadDetail?.offers_teledermatology
        : leadCaptureDropdownData?.find(
            (item) => item?.slug === 'teledermatology'
          )?.values?.[0] || null,
      estimatedWeeklyPatientVolume: editData?.id
        ? leadDetail?.weekly_patient_volume
        : leadCaptureDropdownData?.find(
            (item) => item?.slug === 'patient-volume'
          )?.values?.[0] || null,
      interestAbsNetwork: editData?.id
        ? leadDetail?.professional_network_interest
        : 5,
      interestAAD: editData?.id
        ? leadDetail?.aad_founding_offer_interest
        : leadCaptureDropdownData?.find(
            (item) => item?.slug === 'aad-offer-interest'
          )?.values?.[0] || null,
      interestLevel: editData?.id
        ? leadDetail?.interest_level
        : leadCaptureDropdownData?.find(
            (item) => item?.slug === 'interest-level'
          )?.values?.[0] || null,
      notes: editData?.id ? leadDetail?.notes : '',
      followup: editData?.id ? leadDetail?.followup : '',
      businessCard: editData?.id ? leadDetail?.business_card : null,
      businessCardPreview: editData?.id ? leadDetail?.business_card : null,
      profileImage: editData?.id ? leadDetail?.profile_photo : null,
      profileImagePreview: editData?.id ? leadDetail?.profile_photo : null,
      investmentFocus: editData?.id
        ? leadDetail?.investment_focus?.length > 0
          ? leadDetail?.investment_focus
          : [
              leadCaptureDropdownData?.find(
                (item) => item?.slug === 'investment-focus'
              )?.values?.[0],
            ]
        : leadCaptureDropdownData?.find(
              (item) => item?.slug === 'investment-focus'
            )?.values?.[0]
          ? [
              leadCaptureDropdownData.find(
                (item) => item?.slug === 'investment-focus'
              ).values[0],
            ]
          : [],
      investorDeck: editData?.id
        ? leadDetail?.investor_deck_requested
          ? leadDetail?.investor_deck_requested
          : leadCaptureDropdownData?.find(
              (item) => item?.slug === 'investor-deck'
            )?.values?.[0] || null
        : leadCaptureDropdownData?.find(
            (item) => item?.slug === 'investor-deck'
          )?.values?.[0] || null,
      orgType: editData?.id
        ? leadDetail?.organization_type?.length > 0
          ? leadDetail?.organization_type
          : [
              leadCaptureDropdownData?.find(
                (item) => item?.slug === 'organization-type'
              )?.values?.[0],
            ]
        : leadCaptureDropdownData?.find(
              (item) => item?.slug === 'organization-type'
            )?.values?.[0]
          ? [
              leadCaptureDropdownData.find(
                (item) => item?.slug === 'organization-type'
              ).values[0],
            ]
          : [],
    });
    setPhoneNumber(editData?.id ? leadDetail?.mobile_number : '');
    setCountryCode(editData?.id ? leadDetail?.country_code : '');
  };

  const handleBack = () => {
    onClose();
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormValue((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhoneChange = (value, country) => {
    const countryCode = `+${country?.dialCode}`;
    const contactNumber = value.replace(country?.dialCode, '').trim();

    setPhoneNumber(contactNumber);
    setCountryCode(countryCode);
  };

  const handleChipChange = (key, value) => {
    setFormValue((prev) => ({ ...prev, [key]: value }));
  };

  const handleMultiChipChange = (key, value) => {
    setFormValue((prev) => {
      const existing = prev[key] || [];
      const alreadySelected = existing.some((s) => s?.slug === value?.slug);
      return {
        ...prev,
        [key]: alreadySelected
          ? existing.filter((s) => s?.slug !== value?.slug)
          : [...existing, value],
      };
    });
  };

  const handleFileUpload = (e, type, typePreview) => {
    setFormValue((prev) => ({
      ...prev,
      [type]: e.target.files[0],
      [typePreview]: URL.createObjectURL(e.target.files[0]),
    }));
  };

  const handleSave = () => {
    if (
      !formValue?.fullName ||
      !formValue?.company ||
      !formValue?.titleRole ||
      !formValue?.email ||
      !phoneNumber
    ) {
      showToast('Please fill all the required fields', 'error');
      return;
    }
    const formData = new FormData();
    const userTypeId = formValue?.definesYouBest?.id;

    // Common fields for all user types
    formData.append('full_name', formValue?.fullName || '');
    formData.append('company_organization', formValue?.company || '');
    formData.append('title_role', formValue?.titleRole || '');
    formData.append('email', formValue?.email || '');
    formData.append('country_code', countryCode || '');
    formData.append('mobile_number', phoneNumber || '');
    formData.append(
      'professional_network_interest',
      formValue?.interestAbsNetwork ?? ''
    );

    if (userTypeId) {
      formData.append('user_type', userTypeId);
    }
    if (formValue?.profileImage instanceof File) {
      formData.append('profile_photo', formValue?.profileImage);
    }
    if (formValue?.businessCard instanceof File) {
      formData.append('business_card', formValue?.businessCard);
    }

    // id === 1 (Dermatologist) specific fields
    if (userTypeId === 1) {
      if (formValue?.practiceType?.id) {
        formData.append('practice_type', formValue?.practiceType?.id);
      }
      if (formValue?.currentlyOfferTeledermatology?.id) {
        formData.append(
          'offers_teledermatology',
          formValue?.currentlyOfferTeledermatology?.id
        );
      }
      if (formValue?.estimatedWeeklyPatientVolume?.id) {
        formData.append(
          'weekly_patient_volume',
          formValue?.estimatedWeeklyPatientVolume?.id
        );
      }
      if (formValue?.interestAAD?.id) {
        formData.append(
          'aad_founding_offer_interest',
          formValue?.interestAAD?.id
        );
      }
      if (formValue?.interestLevel?.id) {
        formData.append('interest_level', formValue?.interestLevel?.id);
      }
      formData.append('notes', formValue?.notes || '');
      formData.append('follow_up_date', formValue?.followup || '');
    }

    // id === 2 (Investor) specific fields
    if (userTypeId === 2) {
      (formValue?.investmentFocus || []).forEach((item) => {
        if (item?.id) formData.append('investment_focus', item.id);
      });

      formData.append('investor_deck_requested', formValue?.investorDeck?.id);
    }

    // id === 3 (Industry Partner) specific fields
    if (userTypeId === 3) {
      (formValue?.orgType || []).forEach((item) => {
        if (item?.id) formData.append('organization_type', item.id);
      });
      formData.append('investor_deck_requested', formValue?.investorDeck?.id);
    }

    const body = {
      id: leadDetail?.id,
      detail: formData,
    };

    if (editData?.id) {
      dispatch(
        updateLeadCaptureCallAPI(body, {
          run: true,
          handleFunction: (res) => {
            showToast(res?.message, 'success');
            handleBack();
          },
          authorize: true,
          handleAuthorizeFunction: (res) => {
            ClearTokenRedirectLogin(navigate);
          },
          error: true,
          handleErrorFunction: (res) => {
            showToast(res?.message, 'error');
          },
        })
      );
    } else {
      dispatch(
        createLeadCaptureCallAPI(formData, {
          run: true,
          handleFunction: () => {
            showToast('Lead created successfully', 'success');
            handleBack();
          },
          authorize: true,
          handleAuthorizeFunction: (res) => {
            ClearTokenRedirectLogin(navigate);
          },
          error: true,
          handleErrorFunction: (res) => {
            showToast(res?.message, 'error');
          },
        })
      );
    }
  };

  return (
    <CustomDrawer
      open={open}
      onClose={() => {
        handleBack();
      }}
      width={{ xs: '100vw', md: '70vw', xl: '40vw' }}
    >
      <div className="relative h-full min-h-[400px] flex flex-col">
        <div className="flex-1 overflow-y-auto p-[25px] pb-[15%]">
          <div className="mt-[43px]">
            <div className="flex items-center justify-between gap-[20px]">
              <p className="p-0 m-0 text-[24px] text-[#292D32] family_outfit_semiBold_600 lh-176">
                Lead Capture
              </p>
              <IconButton onClick={handleBack}>
                <CloseIcon />
              </IconButton>
            </div>
            <div className="mt-[24px] flex items-center gap-[16px]">
              <div
                className="w-[100px] h-[100px] rounded-full border-2 border-[#EFE3D7] flex items-center justify-center cursor-pointer overflow-hidden flex-shrink-0 bg-[#F9F4F0]"
                onClick={() => profileImageInputRef.current?.click()}
              >
                {formValue?.profileImagePreview ? (
                  <img
                    src={formValue.profileImagePreview}
                    alt="profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <CameraAltIcon sx={{ fontSize: '24px', color: '#998A7B' }} />
                )}
              </div>
              <input
                type="file"
                accept=".png,.jpg,.jpeg"
                ref={profileImageInputRef}
                className="d-none"
                onChange={(e) => {
                  handleFileUpload(e, 'profileImage', 'profileImagePreview');
                }}
              />
              <div className="flex-1">
                <LabelWithInput
                  label={
                    <>
                      Full Name <span style={{ color: 'red' }}>*</span>
                    </>
                  }
                  placeholder="Full Name"
                  name="fullName"
                  type="text"
                  value={formValue?.fullName}
                  onChange={(e) => handleInputChange(e)}
                />
              </div>
            </div>
            <div className="mt-[24px]">
              <LabelWithInput
                label={
                  <>
                    Company / Organization{' '}
                    <span style={{ color: 'red' }}>*</span>
                  </>
                }
                placeholder="Company / Organization"
                name="company"
                type="text"
                value={formValue?.company}
                onChange={(e) => handleInputChange(e)}
              />
            </div>
            <div className="mt-[18px]">
              <LabelWithInput
                label={
                  <>
                    Title / Role <span style={{ color: 'red' }}>*</span>
                  </>
                }
                placeholder="Title / Role"
                name="titleRole"
                type="text"
                value={formValue?.titleRole}
                onChange={(e) => handleInputChange(e)}
              />
            </div>
            <div className="mt-[18px]">
              <LabelWithInput
                label={
                  <>
                    Email <span style={{ color: 'red' }}>*</span>
                  </>
                }
                placeholder="Email"
                name="email"
                type="text"
                value={formValue?.email}
                onChange={(e) => handleInputChange(e)}
              />
            </div>
            <div className="mt-[18px]">
              <div className="text-[14px] lh-201 family_outfit_medium_500 fc-292D32 mb-[4px]">
                Mobile number <span style={{ color: 'red' }}>*</span>
              </div>
              <PhoneInput
                id="phone-input"
                country={'in'}
                inputProps={{
                  name: 'phoneNumber',
                  required: true,
                }}
                name={'phoneNumber'}
                value={`${countryCode}${phoneNumber}`}
                onChange={(value, country) => handlePhoneChange(value, country)}
                placeholder="Enter phone number"
                inputClass="custom-input-register"
                containerClass="custom-container-register"
              />
            </div>
            <div className="mt-[18px]">
              {formValue?.businessCardPreview ? (
                <div className="relative border-2 border-[#EFE3D7] h-[200px] rounded-[24px] overflow-hidden">
                  <img
                    src={formValue.businessCardPreview}
                    alt="business card"
                    className="w-full h-full object-cover"
                  />
                  <IconButton
                    size="small"
                    onClick={() =>
                      setFormValue((prev) => ({
                        ...prev,
                        businessCard: null,
                        businessCardPreview: null,
                      }))
                    }
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: 'rgba(0,0,0,0.5)',
                      '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: '16px', color: '#fff' }} />
                  </IconButton>
                </div>
              ) : (
                <div
                  className="border-2 border-[#EFE3D7] h-[200px] p-[10px] rounded-[24px] flex items-center justify-center cursor-pointer gap-[10px]"
                  onClick={() => fileInputRef.current.click()}
                >
                  <UploadFileIcon sx={{ fontSize: '30px' }} />
                  <div className="text-[16px] lh-201 family_outfit_regular_400 fc-292D32">
                    Upload business card
                  </div>
                </div>
              )}
              <input
                type="file"
                onChange={(e) => {
                  handleFileUpload(e, 'businessCard', 'businessCardPreview');
                }}
                accept=".png, .jpg, .jpeg"
                ref={fileInputRef}
                className="d-none"
              />
            </div>
            <ChipSelector
              label="What defines you the best?"
              slug="user-type"
              formKey="definesYouBest"
              formValue={formValue}
              dropdownData={leadCaptureDropdownData}
              onChange={handleChipChange}
            />
            {formValue?.definesYouBest?.id === 1 && (
              <ChipSelector
                label="Practice Type"
                slug="practice-type"
                formKey="practiceType"
                formValue={formValue}
                dropdownData={leadCaptureDropdownData}
                onChange={handleChipChange}
              />
            )}
            {formValue?.definesYouBest?.id === 1 && (
              <ChipSelector
                label="Do you currently offer teledermatology?"
                slug="teledermatology"
                formKey="currentlyOfferTeledermatology"
                formValue={formValue}
                dropdownData={leadCaptureDropdownData}
                onChange={handleChipChange}
              />
            )}
            {formValue?.definesYouBest?.id === 1 && (
              <ChipSelector
                label="Estimated Weekly Patient Volume"
                slug="patient-volume"
                formKey="estimatedWeeklyPatientVolume"
                formValue={formValue}
                dropdownData={leadCaptureDropdownData}
                onChange={handleChipChange}
              />
            )}
            {formValue?.definesYouBest?.id === 2 && (
              <MultiChipSelector
                label="Investment Focus"
                slug="investment-focus"
                formKey="investmentFocus"
                formValue={formValue}
                dropdownData={leadCaptureDropdownData}
                onChange={handleMultiChipChange}
              />
            )}
            {formValue?.definesYouBest?.id === 3 && (
              <MultiChipSelector
                label="Organization Type"
                slug="organization-type"
                formKey="orgType"
                formValue={formValue}
                dropdownData={leadCaptureDropdownData}
                onChange={handleMultiChipChange}
              />
            )}
            <div className="mt-[24px]">
              <div className="text-[14px] lh-201 family_outfit_medium_500 fc-292D32 mb-[4px]">
                Interest in Absolut Skin Professional Network
              </div>
              <div className="mt-[24px]">
                <CustomStepper
                  value={formValue?.interestAbsNetwork}
                  onChange={(val) => {
                    setFormValue((prev) => ({
                      ...prev,
                      interestAbsNetwork: val,
                    }));
                  }}
                />
              </div>
            </div>
            {formValue?.definesYouBest?.id === 1 && (
              <ChipSelector
                label="Interested in AAD Founding Dermatologist Offer?"
                slug="aad-offer-interest"
                formKey="interestAAD"
                formValue={formValue}
                dropdownData={leadCaptureDropdownData}
                onChange={handleChipChange}
              />
            )}
            {formValue?.definesYouBest?.id === 1 && (
              <ChipSelector
                label="Interest Level"
                slug="interest-level"
                formKey="interestLevel"
                formValue={formValue}
                dropdownData={leadCaptureDropdownData}
                onChange={handleChipChange}
              />
            )}
            {formValue?.definesYouBest?.id === 1 && (
              <div className="mt-[24px]">
                <div className="text-[14px] lh-201 family_outfit_medium_500 fc-292D32 mb-[8px]">
                  Notes
                </div>
                <LabelWithTextArea
                  placeholder={'Write here...'}
                  height="80"
                  multiline
                  name="notes"
                  Color={'#212426'}
                  padding={'8px'}
                  value={formValue?.notes}
                  onChange={handleInputChange}
                  margintop={'0'}
                />
              </div>
            )}
            {formValue?.definesYouBest?.id === 1 && (
              <div className="mt-[24px]">
                <LabelWithInput
                  label={<>Follow-up Date</>}
                  placeholder="Follow-up Date"
                  name="followup"
                  type="date"
                  value={formValue?.followup}
                  onChange={(e) => handleInputChange(e)}
                />
              </div>
            )}
            {(formValue?.definesYouBest?.id === 2 ||
              formValue?.definesYouBest?.id === 3) && (
              <ChipSelector
                label="Would you like the Investor Deck?"
                slug="investor-deck"
                formKey="investorDeck"
                formValue={formValue}
                dropdownData={leadCaptureDropdownData}
                onChange={handleChipChange}
              />
            )}
          </div>
        </div>
        {/* Absolutely Positioned Bottom Button */}
        <div
          className="mt-[30px] absolute left-0 bottom-0 w-full bg-[#375241] p-[30px] cursor-pointer"
          style={{ zIndex: 10 }}
          onClick={() => {
            handleSave();
          }}
        >
          <div className="text-[24px] text-white family_outfit_semiBold_600 lh-176 text-center flex items-center justify-center gap-[10px]">
            {'Save'}
            {createLeadLoading || updateLeadLoader ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <span
                style={{ width: 24, height: 24, display: 'inline-block' }}
              />
            )}
          </div>
        </div>
      </div>
    </CustomDrawer>
  );
};

export default CreateLead;
